import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ArrowRight, Activity, Globe, Loader2, AlertCircle } from 'lucide-react';

export const AdminHQLogin: React.FC = () => {
    const navigate = useNavigate();
    const [status, setStatus] = useState<'checking' | 'needs_cf_login' | 'error'>('checking');
    const [errorReason, setErrorReason] = useState<string>('');

    // Check circuit breaker first (prevent endless redirect loops)
    useEffect(() => {
        const attemptSync = async () => {
            const lastSyncStr = localStorage.getItem('hq_last_sync');
            if (lastSyncStr) {
                const elapsed = Date.now() - parseInt(lastSyncStr, 10);
                if (elapsed < 3000) {
                    // Loop detected!
                    console.error('[HQ] Circuit Breaker triggered. Redirect loop detected.');
                    setErrorReason('CIRCUIT_BREAKER_LOOP');
                    setStatus('error');
                    localStorage.removeItem('hq_last_sync'); // clear it so they can try again manually later
                    return;
                }
            }
            
            // Mark sync attempt
            localStorage.setItem('hq_last_sync', Date.now().toString());

            try {
                // Try to exchange the CF Access JWT cookie for our app session.
                // This works if the user already passed through CF Access for this domain.
                const res = await fetch('/api/auth/cloudflare-sync', {
                    method: 'GET',
                    credentials: 'include',
                });

                if (res.ok) {
                    const data = await res.json();
                    if (data.success && data.user) {
                        if (data.user.role === 'HOSPITAL_ADMIN') {
                            if (!data.user.hospitalId) {
                                setErrorReason('Tu cuenta de Hospital Admin no tiene un hospital asignado. Contacta al Super Administrador.');
                                setStatus('error');
                                return;
                            }
                            // Seed the staff auth context for the hospital admin
                            const authData = {
                                token: 'cf_session',
                                hospitalName: 'Hospital',
                                hospitalId: data.user.hospitalId,
                                role: data.user.role, expiresAt: Date.now() + 48 * 60 * 60 * 1000,
                                staffId: data.user.id,
                                staffName: data.user.name,
                                staffRole: data.user.role
                            };
                            localStorage.setItem('hospitalstatus_auth', JSON.stringify(authData));
                            navigate('/staff', { replace: true });
                        } else if (data.user.role === 'GROUP_ADMIN') {
                            if (!data.user.groupId) {
                                setErrorReason('Tu cuenta de Group Admin no tiene un grupo corporativo asignado. Contacta al Super Administrador.');
                                setStatus('error');
                                return;
                            }
                            localStorage.setItem('hospitalstatus_admin_ctx', JSON.stringify({
                                id: data.user.id,
                                name: data.user.name,
                                role: data.user.role, expiresAt: Date.now() + 48 * 60 * 60 * 1000,
                                hospitalId: data.user.hospitalId,
                                groupId: data.user.groupId,
                            }));
                            navigate(`/c/${data.user.groupId}/dashboard`, { replace: true });
                        } else {
                            // SUPER_ADMIN
                            localStorage.setItem('hospitalstatus_admin_ctx', JSON.stringify({
                                id: data.user.id,
                                name: data.user.name,
                                role: data.user.role, expiresAt: Date.now() + 48 * 60 * 60 * 1000,
                            }));
                            navigate('/admin/dashboard', { replace: true });
                        }
                        return;
                    }
                } else if (res.status === 403) {
                    // Authenticated by CF but not authorized in our DB
                    const data = await res.json().catch(() => ({}));
                    setErrorReason(data.error || 'ERROR_403_DB_DENIED');
                    setStatus('error');
                    return;
                }

                // No CF cookie yet — show button to trigger CF Access login
                setStatus('needs_cf_login');
            } catch (e: any) {
                console.error('[HQ] CF Sync error:', e);
                setErrorReason(e.message || 'NETWORK_OR_FETCH_ERROR');
                setStatus('needs_cf_login');
            }
        };

        attemptSync();
    }, [navigate]);

    const handleContinueWithCloudflare = () => {
        // Redirigir a /hq/ para forzar el trigger nativo de Cloudflare Zero Trust
        // de acuerdo a la configuración de la App (que espera la diagonal final).
        window.location.href = '/hq/';
    };

    return (
        <div className="min-h-screen bg-black flex flex-col justify-center relative overflow-hidden font-sans">
            {/* Premium Apple-style Liquid Aurora Background */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div 
                    className="absolute w-[80vw] h-[80vw] bg-emerald-500/20 blur-[130px] mix-blend-screen"
                    style={{ 
                        animation: 'liquid-spin 18s linear infinite', 
                        top: '-30%', left: '-20%',
                        borderRadius: '40% 60% 60% 40% / 70% 30% 70% 30%'
                    }}
                />
                <div 
                    className="absolute w-[90vw] h-[90vw] bg-indigo-600/25 blur-[150px] mix-blend-screen"
                    style={{ 
                        animation: 'liquid-spin-reverse 22s linear infinite', 
                        top: '10%', right: '-40%',
                        borderRadius: '60% 40% 30% 70% / 50% 60% 40% 50%'
                    }}
                />
                <div 
                    className="absolute w-[70vw] h-[70vw] bg-purple-600/20 blur-[130px] mix-blend-screen"
                    style={{ 
                        animation: 'liquid-spin 20s linear infinite', 
                        bottom: '-40%', left: '10%',
                        borderRadius: '50% 50% 70% 30% / 30% 50% 70% 50%'
                    }}
                />
                {/* Dark overlay to ensure contrast and premium feel */}
                <div className="absolute inset-0 bg-black/50 backdrop-blur-[40px]"></div>
            </div>
            
            <style>{`
                @keyframes liquid-spin {
                    0% { transform: rotate(0deg) scale(1) translate(0, 0); }
                    33% { transform: rotate(120deg) scale(1.1) translate(5vw, 5vh); }
                    66% { transform: rotate(240deg) scale(0.9) translate(-5vw, 2vh); }
                    100% { transform: rotate(360deg) scale(1) translate(0, 0); }
                }
                @keyframes liquid-spin-reverse {
                    0% { transform: rotate(360deg) scale(1.1) translate(0, 0); }
                    33% { transform: rotate(240deg) scale(0.9) translate(-5vw, -5vh); }
                    66% { transform: rotate(120deg) scale(1.1) translate(2vw, 5vh); }
                    100% { transform: rotate(0deg) scale(1.1) translate(0, 0); }
                }
            `}</style>

            <div className="w-full max-w-[420px] mx-auto px-6 relative z-10">

                {/* Logo & Branding */}
                <div className="flex flex-col items-center mb-12 animate-fade-in">
                    <div className="w-16 h-16 bg-neutral-900 border border-neutral-800 rounded-2xl flex items-center justify-center shadow-xl mb-6 relative group">
                        <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500/20 to-emerald-500/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        <Activity className="w-8 h-8 text-neutral-300 relative z-10" />
                    </div>
                    <h1 className="text-3xl font-bold tracking-tight text-white mb-2">Hospital Status HQ</h1>
                    <p className="text-neutral-500 text-sm font-medium tracking-wide">ENTERPRISE COMMAND CENTER</p>
                </div>

                {/* Main Card */}
                <div className="relative group p-[1px] rounded-[1.5rem] overflow-hidden animate-slide-up">
                    {/* Glowing rotating border */}
                    <div className="absolute inset-[-50%] bg-[conic-gradient(from_0deg_at_50%_50%,transparent_0%,transparent_75%,rgba(16,185,129,0.8)_100%)] animate-[spin_3s_linear_infinite]" />
                    
                    {/* Inner Card */}
                    <div className="relative bg-neutral-950/90 backdrop-blur-xl rounded-[calc(1.5rem-1px)] p-8 shadow-2xl">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-[1px] bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent"></div>

                        <div className="text-center mb-8">
                            <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full mb-4 border ${status === 'error' ? 'bg-red-500/10 border-red-500/20' : 'bg-emerald-500/10 border-emerald-500/20'}`}>
                                {status === 'error'
                                    ? <AlertCircle className="w-6 h-6 text-red-400" />
                                    : <ShieldCheck className="w-6 h-6 text-emerald-500" />
                                }
                            </div>
                            {status === 'error' ? (
                                <div className="text-center p-6 space-y-4">
                                    <AlertCircle className="w-12 h-12 text-red-400 mx-auto" />
                                    <h2 className="text-xl font-bold text-white">Acceso Suspendido</h2>
                                    <p className="text-slate-400 text-sm">El acceso automático falló o no tienes permisos. Intenta nuevamente o usa el Portal Estándar.</p>
                                    <div className="mt-2 text-xs font-mono text-red-400/80 bg-red-950/30 p-2 rounded border border-red-500/20 break-words">
                                        [Razón: {errorReason}]
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <h2 className="text-xl font-semibold text-neutral-200 mb-2">Acceso Seguro</h2>
                                    <p className="text-neutral-400 text-sm leading-relaxed">
                                        Verificación requerida para acceso al panel central.
                                    </p>
                                </>
                            )}
                        </div>

                        {status === 'checking' && (
                            <div className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-6 flex flex-col items-center justify-center gap-3">
                                <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
                                <p className="text-sm font-medium text-neutral-400">Verificando identidad de red...</p>
                            </div>
                        )}

                        {status === 'needs_cf_login' && (
                            <div className="space-y-4">
                                <button
                                    onClick={handleContinueWithCloudflare}
                                    className="w-full bg-neutral-100 hover:bg-white text-neutral-900 rounded-2xl py-4 px-6 flex items-center justify-between font-bold text-sm transition-all group shadow-[0_0_20px_rgba(255,255,255,0.05)] hover:shadow-[0_0_30px_rgba(255,255,255,0.1)] active:scale-95"
                                >
                                    <span className="flex items-center gap-3">
                                        <Globe className="w-5 h-5 text-neutral-500 group-hover:text-emerald-600 transition-colors" />
                                        Acceso de Administrador
                                    </span>
                                    <ArrowRight className="w-4 h-4 text-neutral-400 group-hover:translate-x-1 transition-transform" />
                                </button>
    
                                <p className="text-center text-[10px] uppercase tracking-widest text-neutral-600 font-bold mt-6">
                                    Securing Healthcare Data
                                </p>
                            </div>
                        )}

                        {status === 'error' && (
                            <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 text-center">
                                <p className="text-red-400 text-sm font-medium">Contacta al Super Administrador del sistema.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer Link */}
                <div className="text-center mt-8 animate-fade-in flex justify-center">
                    <button
                        onClick={() => navigate('/login')}
                        className="text-xs font-medium text-neutral-500 hover:text-neutral-300 transition-colors"
                    >
                        Volver al Portal Estándar
                    </button>
                </div>
            </div>
        </div>
    );
};
